import { DiceRollActionTypes, isDebug, RollTos } from "@/constants";
import { DiceOperations } from "@/enums";
import { useDiceRollPrivacyStore } from "@/hooks/useDiceRollPrivacyStore";
import type {
  DiceRollEventPayload,
  DieTypeQuantities,
  RollDiceParams,
  RollDicePayload,
  RollResult,
} from "@/types";
import { key } from "@dndbeyond/message-broker-lib/dist/lib/constants";
import { v4 as uuid } from "uuid";

export const rollDice = (
  {
    diceNotation,
    action,
    rollType = "roll",
    rollKind = "",
    entityId,
    entityType,
    userId,
    setId,
    gameId,
    isDm,
    name,
    avatarUrl,
    isShared3dDiceEnabled,
    valueOverride,
    messageTarget = Number(userId),
    operation,
    operand,
  }: RollDiceParams,
  hasWorkerErrored: boolean,
  isWorkerReady: boolean,
  callback?: (payload: DiceRollEventPayload) => void,
) => {
  const kind = rollKind.toLowerCase();
  const rollTo =
    useDiceRollPrivacyStore.getState().getDiceRollPrivacy(Number(userId))
      ?.rollTo || RollTos.Everyone;
  const advDisadv =
    kind === "advantage" ? "adv" : kind === "disadvantage" ? "disadv" : null;
  const isCrit = kind === "critical hit";
  const entries = diceNotation.split(/(?=[+-])/).map((entry) => {
    const [quantity, die] = entry.split("d");

    return [
      die ? `d${die}` : "constant",
      Number(quantity) * (die && isCrit ? 2 : 1) + (die && advDisadv ? 1 : 0),
    ] as const;
  });
  const constant = entries.find(([die]) => die === "constant")?.[1] || 0;
  const diceToRoll = entries.reduce(
    (acc, [die, quantity]) => {
      acc[die as keyof DieTypeQuantities] = quantity;

      return acc;
    },
    {} as Record<keyof DieTypeQuantities, number>,
  );
  const result: RollResult = {
    constant,
    text: "",
    total: 0,
    values: [],
  };
  const messageScope = rollTo === RollTos.Self ? "userId" : "gameId";
  const rollId = uuid();
  const hasDiceToRoll =
    Object.keys(diceToRoll).filter((k) => k !== "constant").length !== 0;
  const isDeferred = Boolean(
    isShared3dDiceEnabled &&
    !hasWorkerErrored &&
    isWorkerReady &&
    hasDiceToRoll,
  );
  const eventType = isDeferred
    ? DiceRollActionTypes.Deferred
    : DiceRollActionTypes.Fulfilled;
  const eventPayloadNoTotal: Omit<DiceRollEventPayload, "total"> = {
    rollId,
    diceRolled: {},
    results: {},
    action,
    type: rollType,
    kind,
    diceSet: setId,
    audience: rollTo,
    isDm,
    eventType,
  };
  const payload: RollDicePayload = {
    data: {
      action,
      context: {
        entityId,
        entityType,
        messageScope,
        messageTarget: String(messageTarget),
        name,
        avatarUrl,
      },
      rollId,
      rolls: [
        {
          diceNotation: {
            constant,
            set: entries
              .map(([d, quantity], i) => {
                if (d === "constant") {
                  result.text += `+${constant}`;
                  result.total += constant;

                  return;
                }

                const die = d as keyof DieTypeQuantities;
                const rolls: number[] = [];
                const isLastEntry = i === entries.length - 1;

                return {
                  count: diceToRoll[die],
                  dice: [...Array(quantity)].map((_, j) => {
                    eventPayloadNoTotal.diceRolled[die] = diceToRoll[die];
                    const dieValue =
                      valueOverride ??
                      Math.floor(Math.random() * Number(die.slice(1)) + 1);
                    eventPayloadNoTotal.results[die] = [
                      ...(eventPayloadNoTotal.results[die] || []),
                      dieValue,
                    ];
                    const isLastDie = j === quantity - 1;

                    if (operand || advDisadv) {
                      rolls.push(dieValue);

                      if (j === 0) {
                        result.text += "(";
                      }
                    }

                    result.text += dieValue;

                    if (operand || advDisadv) {
                      result.text += isLastDie ? ")" : ",";
                    }

                    if (
                      !(operand || advDisadv) &&
                      !(isLastDie && (constant || isLastEntry))
                    ) {
                      result.text += "+";
                    }

                    const getKeepTotal = (
                      operation: DiceOperations,
                      operand: number,
                    ) =>
                      rolls
                        .sort((a, b) =>
                          operation === DiceOperations.Min ? a - b : b - a,
                        )
                        .slice(0, operand)
                        .reduce((acc, num) => acc + num, 0);

                    result.total += (() => {
                      if ((operand || advDisadv) && !isLastDie) return 0;

                      if (operand) {
                        return getKeepTotal(operation!, operand);
                      }

                      if (advDisadv) {
                        return getKeepTotal(
                          advDisadv === "adv"
                            ? DiceOperations.Max
                            : DiceOperations.Min,
                          1,
                        );
                      }

                      return dieValue;
                    })();

                    result.values.push(dieValue);

                    return {
                      dieType: die,
                      dieValue,
                    };
                  }),
                  dieType: die,
                  operation: (() => {
                    if (operation) return operation;

                    if (advDisadv) {
                      return advDisadv === "adv"
                        ? DiceOperations.Max
                        : DiceOperations.Min;
                    }

                    return DiceOperations.Sum;
                  })(),
                  operand,
                };
              })
              .filter(Boolean),
          },
          rollKind: kind,
          rollType,
          result,
        },
      ],
      setId,
    },
    dateTime: String(Date.now()),
    entityId,
    entityType,
    eventType,
    ...(gameId && { gameId }),
    id: uuid(),
    messageScope,
    messageTarget: String(messageTarget),
    persist: !isDeferred,
    source: "web",
    ...(userId && { userId }),
  };

  window[key]?.dispatch(payload);
  callback?.({
    ...eventPayloadNoTotal,
    total: result.total,
  });

  if (isDebug) {
    console.log("Rolled Dice Payload:", payload);
  }

  return payload;
};
